import { Route, Navigate, useParams } from 'react-router-dom';
import ProtectedRoute from 'shared/routes/Protected-Route';
import LegacyTenantPathRedirect from 'shared/routes/LegacyTenantPathRedirect';
import Login from 'platform/auth/pages/login';
import ResetPassword from 'platform/auth/pages/Reset-password';
import FirstLoginChangePassword from 'platform/auth/pages/FirstLoginChangePassword';
import NotFoundPage from 'platform/auth/pages/NotFoundPage';
import Dashboard from 'platform/dashboard/pages/home/index';
import Team from 'platform/dashboard/pages/team/index';
import Bar from 'platform/dashboard/pages/bar/index';
import Pie from 'platform/dashboard/pages/pie/index';
import Line from 'platform/dashboard/pages/line/index';
import FAQ from 'platform/dashboard/pages/faq/index';
import Calendar from 'platform/dashboard/pages/calendar/calendar';
import Geography from 'platform/dashboard/pages/geography/index';
import Sessions from 'platform/auth/pages/session';
import UserDetails from 'platform/user/pages/user-details';
import UserTable from 'platform/user/pages/users';
import ProfileDetails from 'platform/user/pages/profileDetails';
import Tenants from 'platform/tenant/pages/Tenants';
import TenantDetails from 'platform/tenant/pages/tenantDetails';
import TenantUsers from 'platform/user/pages/tenant-users/TenantUsers';
import TenantUsersForAdmin from 'platform/user/pages/tenant-users/TenantUsersForAdmins';
import GroupPage from 'platform/rbac/pages/group/group';
import GroupPermission from 'platform/rbac/pages/group/GroupPermission';
import UsersAssignedToGroup from 'platform/rbac/pages/group/UsersAssignedToGroup';
import UsersPermissionsReportPage from 'platform/rbac/pages/group/UsersPermissionsReportPage';
import PermissionsPage from 'platform/rbac/pages/permissions/permissions';
import UserAuditTrialPage from 'platform/audit/pages/UserAuditTrialPage';
import TenantServicesPage from 'platform/catalog/pages/tenantService';
import TenantSettingsPage from 'platform/tenant/pages/TenantSettingsPage';
import { TENANT_ADMIN_PERMISSION } from 'shared/utils/sessionPermissions';

function LegacyTenantUsersAdminRedirect() {
  const { tenantKey, tenantId } = useParams();
  const prefix = tenantKey ? `/${encodeURIComponent(tenantKey)}` : '';
  return (
    <Navigate
      to={`${prefix}/tenant-users-admin`}
      replace
      state={{ registryTenantId: tenantId }}
    />
  );
}

/** Backend Role enum plus legacy UI role names used in older screens */
const DASHBOARD_ROLES = [
  'ADMIN',
  'SYSTEMADMIN',
  'MANAGER',
  'SERVICE-ADMIN',
  'USER',
  'SUPPLIER',
  'TEACHER',
  'PARENT',
  'SECRETARY',
  'ACCOUNTANT',
  'CHIEF',
  'ACADEMIC',
  'CLASSTEACHER',
];

const PROFILE_ROLES = [
  'ADMIN',
  'SYSTEMADMIN',
  'USER',
  'MANAGER',
  'SUPPLIER',
  'SERVICEADMIN',
  'SERVICE-ADMIN',
  'TEACHER',
  'PARENT',
  'ACCOUNTANT',
  'CHIEF',
  'ACADEMIC',
  'SECRETARY',
  'CLASSTEACHER',
];

/** Paths that stay tenant-prefixed after login: /{tenantName}/dashboard */
const TK = '/:tenantKey';

/** Auth, tenant admin, RBAC, dashboard — everything except business services */
export const platformRoutes = (
  <>
    {/* Public login (no app chrome) */}
    <Route path="/" element={<Login />} />
    <Route path="/login" element={<Login />} />
    <Route path="/login/:tenantId" element={<Login />} />
    <Route path="/resetPassword" element={<ResetPassword />} />
    <Route path="/resetPassword/:tenantId" element={<ResetPassword />} />
    <Route path={`${TK}`} element={<Login />} />

    <Route
      path={`${TK}/first-login/change-password`}
      element={<ProtectedRoute element={<FirstLoginChangePassword />} />}
    />
    <Route path="/first-login/change-password" element={<LegacyTenantPathRedirect />} />

    <Route
      path={`${TK}/dashboard`}
      element={<ProtectedRoute element={<Dashboard />} allowedRoles={DASHBOARD_ROLES} />}
    />
    <Route path="/dashboard" element={<LegacyTenantPathRedirect />} />

    <Route path={`${TK}/team`} element={<ProtectedRoute element={<Team />} />} />
    <Route path="/team" element={<LegacyTenantPathRedirect />} />

    <Route
      path={`${TK}/sessions/:userId`}
      element={
        <ProtectedRoute
          element={<Sessions />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'MANAGER', 'SERVICE-ADMIN', 'CHIEF', 'ACADEMIC']}
          allowedPermissions={[TENANT_ADMIN_PERMISSION]}
        />
      }
    />
    <Route path="/sessions/:userId" element={<LegacyTenantPathRedirect />} />

    <Route
      path={`${TK}/users`}
      element={
        <ProtectedRoute
          element={<UserTable />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'MANAGER', 'SERVICE-ADMIN', 'ACADEMIC', 'CHIEF', 'ACADEMIC']}
          allowedPermissions={[TENANT_ADMIN_PERMISSION]}
        />
      }
    />
    <Route path="/users" element={<LegacyTenantPathRedirect />} />

    <Route
      path={`${TK}/userDetails/:userId`}
      element={
        <ProtectedRoute
          element={<UserDetails />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'MANAGER', 'SERVICE-ADMIN', 'CHIEF', 'ACADEMIC']}
          allowedPermissions={[TENANT_ADMIN_PERMISSION]}
        />
      }
    />
    <Route path="/userDetails/:userId" element={<LegacyTenantPathRedirect />} />

    <Route
      path={`${TK}/profileDetails`}
      element={
        <ProtectedRoute element={<ProfileDetails />} allowedRoles={PROFILE_ROLES} />
      }
    />
    <Route path="/profileDetails" element={<LegacyTenantPathRedirect />} />
    <Route
      path={`${TK}/profileDetails/:userId`}
      element={
        <ProtectedRoute element={<ProfileDetails />} allowedRoles={PROFILE_ROLES} />
      }
    />
    <Route path="/profileDetails/:userId" element={<LegacyTenantPathRedirect />} />

    <Route
      path={`${TK}/tenants`}
      element={
        <ProtectedRoute
          element={<Tenants />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'MANAGER']}
          allowedPermissions={[TENANT_ADMIN_PERMISSION]}
        />
      }
    />
    <Route path="/tenants" element={<LegacyTenantPathRedirect />} />
    <Route
      path={`${TK}/tenants/:tenantId/details`}
      element={
        <ProtectedRoute
          element={<TenantDetails />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'MANAGER']}
          allowedPermissions={[TENANT_ADMIN_PERMISSION]}
        />
      }
    />
    <Route path="/tenants/:tenantId/details" element={<LegacyTenantPathRedirect />} />

    <Route
      path={`${TK}/tenant-users/:tenantId`}
      element={
        <ProtectedRoute
          element={<TenantUsers />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'MANAGER', 'SERVICE-ADMIN']}
          allowedPermissions={[TENANT_ADMIN_PERMISSION]}
        />
      }
    />
    <Route path="/tenant-users/:tenantId" element={<LegacyTenantPathRedirect />} />
    <Route
      path={`${TK}/tenant-users-admin/:tenantId`}
      element={<LegacyTenantUsersAdminRedirect />}
    />
    <Route path="/tenant-users-admin/:tenantId" element={<LegacyTenantPathRedirect />} />
    <Route
      path={`${TK}/tenant-users-admin`}
      element={
        <ProtectedRoute
          element={<TenantUsersForAdmin />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'MANAGER', 'SERVICE-ADMIN']}
          allowedPermissions={[TENANT_ADMIN_PERMISSION]}
        />
      }
    />
    <Route path="/tenant-users-admin" element={<LegacyTenantPathRedirect />} />

    <Route
      path={`${TK}/groups`}
      element={
        <ProtectedRoute
          element={<GroupPage />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'MANAGER', 'SERVICE-ADMIN']}
          allowedPermissions={[TENANT_ADMIN_PERMISSION]}
        />
      }
    />
    <Route path="/groups" element={<LegacyTenantPathRedirect />} />
    <Route
      path={`${TK}/groups/users-permissions-report`}
      element={
        <ProtectedRoute
          element={<UsersPermissionsReportPage />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'MANAGER', 'SERVICE-ADMIN']}
          allowedPermissions={[TENANT_ADMIN_PERMISSION]}
        />
      }
    />
    <Route path="/groups/users-permissions-report" element={<LegacyTenantPathRedirect />} />
    <Route
      path={`${TK}/groups/:groupId/permissions`}
      element={
        <ProtectedRoute
          element={<GroupPermission />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'MANAGER', 'SERVICE-ADMIN']}
          allowedPermissions={[TENANT_ADMIN_PERMISSION]}
        />
      }
    />
    <Route path="/groups/:groupId/permissions" element={<LegacyTenantPathRedirect />} />
    <Route
      path={`${TK}/groups/:groupId/users-assigned`}
      element={
        <ProtectedRoute
          element={<UsersAssignedToGroup />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'MANAGER', 'SERVICE-ADMIN']}
          allowedPermissions={[TENANT_ADMIN_PERMISSION]}
        />
      }
    />
    <Route path="/groups/:groupId/users-assigned" element={<LegacyTenantPathRedirect />} />
    <Route
      path={`${TK}/permissions`}
      element={
        <ProtectedRoute
          element={<PermissionsPage />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'MANAGER', 'SERVICE-ADMIN']}
          allowedPermissions={[TENANT_ADMIN_PERMISSION]}
        />
      }
    />
    <Route path="/permissions" element={<LegacyTenantPathRedirect />} />
    <Route
      path={`${TK}/user-audit-trials`}
      element={
        <ProtectedRoute
          element={<UserAuditTrialPage />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'SERVICEADMIN', 'SERVICE-ADMIN']}
        />
      }
    />
    <Route path="/user-audit-trials" element={<LegacyTenantPathRedirect />} />
    <Route
      path={`${TK}/services`}
      element={
        <ProtectedRoute
          element={<TenantServicesPage />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'MANAGER']}
          allowedPermissions={[TENANT_ADMIN_PERMISSION]}
        />
      }
    />
    <Route path="/services" element={<LegacyTenantPathRedirect />} />
    <Route
      path={`${TK}/settings`}
      element={
        <ProtectedRoute
          element={<TenantSettingsPage />}
          allowedRoles={['ADMIN', 'SYSTEMADMIN', 'SERVICE-ADMIN', 'SERVICEADMIN']}
        />
      }
    />
    <Route path="/settings" element={<LegacyTenantPathRedirect />} />

    <Route path={`${TK}/bar`} element={<ProtectedRoute element={<Bar />} />} />
    <Route path="/bar" element={<LegacyTenantPathRedirect />} />
    <Route path={`${TK}/pie`} element={<ProtectedRoute element={<Pie />} />} />
    <Route path="/pie" element={<LegacyTenantPathRedirect />} />
    <Route path={`${TK}/line`} element={<ProtectedRoute element={<Line />} />} />
    <Route path="/line" element={<LegacyTenantPathRedirect />} />
    <Route path={`${TK}/faq`} element={<ProtectedRoute element={<FAQ />} />} />
    <Route path="/faq" element={<LegacyTenantPathRedirect />} />
    <Route path={`${TK}/calendar`} element={<ProtectedRoute element={<Calendar />} />} />
    <Route path="/calendar" element={<LegacyTenantPathRedirect />} />
    <Route path={`${TK}/geography`} element={<ProtectedRoute element={<Geography />} />} />
    <Route path="/geography" element={<LegacyTenantPathRedirect />} />

    <Route path={`${TK}/notFound`} element={<NotFoundPage />} />
    <Route path="/notFound" element={<NotFoundPage />} />
  </>
);

export default platformRoutes;
